import React, {useRef} from 'react';
import {Button, EMPHASIS, FlexRow} from '@forecast-it/design-system';
import {FormattedMessage, useIntl} from 'react-intl';
import {useReportBuilder} from '../ReportBuilderContext';
import type {DiscardOutcome, PublishOutcome} from '../ReportBuilderContext';
import {BookmarkIcon, BOOKMARK_COLOR_DISABLED} from '../../shared/icons/BookmarkIcon';
import {createToast} from '../../../forecast-app/shared/components/toasts/another-toast/toaster';
import {ReportDownloadMenu} from './ReportDownloadMenu';
import {ReportShareButton} from './ReportShareButton';
import {showDiscardReportDraftModal} from '../DiscardReportDraftModal';
import {ReportPeriodControls} from './ReportPeriodControls';

/** Matches the dwell time every other saved-report toast in the app uses, so feedback here reads
 * the same as saving a Standard report. Kept in step with the download toast's own duration in
 * `useReportExport`, so the two do not linger for different lengths of time. */
const SAVE_TOAST_DURATION_MS = 5000;

/** Copy for an outcome that could not be confirmed, and the fallback for an outcome the maps below do not
 * carry: a member added to either outcome union without touching its map still warns that the state could
 * not be confirmed instead of being read as success. */
const PUBLISH_UNCONFIRMED_MESSAGE_ID = 'ai_report_builder.publish_unconfirmed';
const DISCARD_UNCONFIRMED_MESSAGE_ID = 'ai_report_builder.discard_unconfirmed';

/** Warning copy per non-success publish outcome. Each outcome gets its own sentence because each is a
 * different truth: 'locked' and 'failed' both leave the working copy exactly where it is, while
 * 'unconfirmed' claims nothing either way — the call never settled, so the publish may still land, and the
 * only honest instruction is to reload the page and look. The map picks the warning and nothing else: the
 * success path is an explicit check for 'published', so an outcome this map does not carry warns with
 * `PUBLISH_UNCONFIRMED_MESSAGE_ID` rather than being read as a publish that happened. */
const PUBLISH_WARNING_MESSAGE_IDS: Partial<Record<PublishOutcome, string>> = {
	locked: 'ai_report_builder.locked_by_other_editor',
	failed: 'ai_report_builder.publish_failed',
	unconfirmed: PUBLISH_UNCONFIRMED_MESSAGE_ID,
};

/** Warning copy per non-success discard outcome. 'locked' and 'failed' mean nothing was discarded, so
 * the editor stays put. 'reload_failed' means the draft WAS dropped but the reverted definition could
 * not be read back, so the copy says the changes are gone and the report needs reloading.
 * 'unconfirmed' means the call never settled, so whether anything was discarded is unknown. Edit mode
 * is kept for all of them: what is on screen is a working copy view mode would present as the stored
 * report. */
const DISCARD_WARNING_MESSAGE_IDS: Partial<Record<DiscardOutcome, string>> = {
	locked: 'ai_report_builder.locked_by_other_editor',
	failed: 'ai_report_builder.discard_failed',
	reload_failed: 'ai_report_builder.discard_reload_failed',
	unconfirmed: DISCARD_UNCONFIRMED_MESSAGE_ID,
};

/** Top bar shown in edit mode: the reporting-period date range, a single-button Download (neutral
 * field style with an inline chevron) whose menu exports the report, a Share button visible only
 * to an editor that opens the sharing dialog once the report is saved (disabled with a tooltip on an
 * unsaved draft), a Discard button (enabled only while a draft is open, always confirmed), and a
 * Publish button wired to `useReportBuilder().publish()`. Publish both saves the working copy and
 * makes it the version readers execute, then returns to view mode — it replaces the earlier Save
 * Report action, so there is no way to persist a working copy without handing it to readers.
 * Changing the period rewrites the definition's date filter, so the table re-runs for the new
 * window. The report name lives in `ReportPageHeader` (rendered above this bar in
 * `ReportBuilderSurface`), not here. */
export function EditTopBar(): JSX.Element {
	const {formatMessage} = useIntl();
	const {
		dirty,
		draftState,
		publish,
		publishing,
		publishOwed,
		reloadRequired,
		discard,
		discarding,
		nameValid,
		setMode,
		savedReportId,
		commitPendingName,
	} = useReportBuilder();

	// Publish needs a usable name and one of: the working copy has never been saved at all, there are
	// unsaved edits, an open draft, or a publish still owed from a previous attempt whose save landed
	// but whose publish did not. A never-saved draft (e.g. straight off the chat card) always has
	// something to hand readers, even with zero edits made to it — it does not exist in the Reports list
	// yet, so publishing it is not a repeat of anything. An existing, unchanged report with none of the
	// other three has nothing new to hand readers, so publishing it would only add an identical version.
	// `reloadRequired` vetoes all of that: what is on screen is either known to be stale (a discard
	// dropped the draft but its re-read failed) or no longer known to match storage at all (a lifecycle
	// call whose outcome could not be confirmed), and publishing it would push content the editor was
	// never told was still there.
	const canPublish =
		nameValid &&
		!publishing &&
		!reloadRequired &&
		(savedReportId === null || dirty || draftState === 'DRAFT' || publishOwed);

	const reloadRequiredRef = useRef(reloadRequired);
	reloadRequiredRef.current = reloadRequired;

	// Only the one outcome that means the publish actually happened switches to view mode and claims
	// success. Everything else warns — including an outcome this build does not recognise, which must never
	// be read as a publish that landed.
	const handlePublish = (): void => {
		// Commits an open, uncommitted rename before publishing (and reads back what is now the name):
		// `definition.name` from this render's context snapshot would still be the pre-commit value even
		// after `publish()` resolves, since committing happens inside the provider, not by React
		// re-rendering this closure. `publish()` (via `save()`) commits it again internally regardless of
		// whether this call site does. That second call is not a no-op the way this comment used to
		// claim: it runs from the same render's closure, so it still sees the staged name. Re-applying
		// the same name is harmless — `commitPendingName` returns early once the staged value matches
		// the stored one — and it is what keeps the toast and the saved report agreeing about the name.
		const publishedName = commitPendingName();
		void publish().then(outcome => {
			if (outcome === 'published') {
				createToast({
					duration: SAVE_TOAST_DURATION_MS,
					message: formatMessage({id: 'ai_report_builder.published_toast'}, {entity: publishedName}),
				});
				setMode('view');
				return;
			}
			createToast({
				duration: SAVE_TOAST_DURATION_MS,
				warning: true,
				message: formatMessage({id: PUBLISH_WARNING_MESSAGE_IDS[outcome] ?? PUBLISH_UNCONFIRMED_MESSAGE_ID}),
			});
		});
	};

	const handleDiscard = (): void => {
		showDiscardReportDraftModal({
			onConfirm: () => {
				// Read through a ref, not the render's own `reloadRequired`: the confirm dialog outlives the render
				// that opened it, so the surface can go out of step while it is up. Discarding from there would act
				// on a report whose stored state is unknown, which is exactly what the disabled button prevents.
				if (reloadRequiredRef.current) {
					createToast({
						duration: SAVE_TOAST_DURATION_MS,
						warning: true,
						message: formatMessage({id: 'ai_report_builder.definition_out_of_step'}),
					});
					return;
				}
				void discard().then(outcome => {
					if (outcome === 'discarded') {
						setMode('view');
						return;
					}
					createToast({
						duration: SAVE_TOAST_DURATION_MS,
						warning: true,
						message: formatMessage({id: DISCARD_WARNING_MESSAGE_IDS[outcome] ?? DISCARD_UNCONFIRMED_MESSAGE_ID}),
					});
				});
			},
		});
	};

	return (
		<FlexRow justifyContent={'space-between'} alignItems={'center'} data-cy={'ai-report-edit-top-bar'}>
			{/* The toolbar is space-between: the period controls hug the left, the actions hug the right. DS
			 * FlexRow defaults to flex:1, so both groups would each take 50% and drift to mid-row; both are
			 * pinned to flex:0 0 auto (the left group inside `ReportPeriodControls` itself) so they hug their
			 * content and space-between pushes them to the true edges. */}
			<ReportPeriodControls />
			<FlexRow alignItems={'center'} gap={'s'} flex={'0 0 auto'}>
				<ReportDownloadMenu />
				<ReportShareButton />
				{/* Discard is gated on `reloadRequired` for the same reason Publish is: with what is on screen
				 * out of step with what is stored, there is no draft the editor can be told this button drops. */}
				<Button
					emphasis={EMPHASIS.MONO}
					disabled={draftState !== 'DRAFT' || discarding || reloadRequired}
					onPress={handleDiscard}
					data-cy={'ai-report-discard-button'}
				>
					{formatMessage({id: 'ai_report_builder.discard'})}
				</Button>
				<Button emphasis={'high'} disabled={!canPublish} onPress={handlePublish} data-cy={'ai-report-publish-button'}>
					{/* the design: Publish carries a leading bookmark glyph. The DS icon set
					 * has no bookmark; reuse the shared BookmarkIcon (shared/icons/BookmarkIcon) that
					 * the agent chat also uses for "Your Saved Prompts". White on the enabled dark-blue high-emphasis
					 * button; the muted token when disabled (clean) so it reads with the greyed label. */}
					<FlexRow alignItems={'center'} gap={'xs'}>
						<BookmarkIcon color={canPublish ? '#ffffff' : BOOKMARK_COLOR_DISABLED} />
						<FormattedMessage id={'ai_report_builder.publish'} />
					</FlexRow>
				</Button>
			</FlexRow>
		</FlexRow>
	);
}
